/** Small shared pieces. Kept deliberately few - a screen that needs
 *  something unusual writes it inline rather than growing a component
 *  library nobody asked for. */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

export function Card({
  title,
  actions,
  footer,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="ch">
          {typeof title === "string" ? <h3>{title}</h3> : title}
          {actions && (
            <>
              <div className="sp" />
              {actions}
            </>
          )}
        </div>
      )}
      <div className="cb">{children}</div>
      {footer && <div className="cf">{footer}</div>}
    </div>
  );
}

export function Tile({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: ReactNode;
  detail?: string;
  tone?: "ok" | "ba";
}) {
  return (
    <div className={`tile ${tone ?? ""}`}>
      <span className="lab">{label}</span>
      <span className="v">{value}</span>
      {detail && <span className="d">{detail}</span>}
    </div>
  );
}

export function Pill({ tone = "nu", children }: { tone?: string; children: ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** Errors are shown, never swallowed. A blank screen with a console error is
 *  the worst possible outcome for someone mid-task. */
export function ErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="banner bad">
      <b>Problem:</b> {error}
    </div>
  );
}

export const fmt = {
  n: (v: number | undefined | null) => (v ?? 0).toLocaleString("en-US"),
  pct: (v: number | undefined | null) => `${(v ?? 0).toFixed(1)}%`,
  when: (iso?: string | null) => {
    if (!iso) return "never";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    // Signed, and read as a distance rather than an age. Half the fields that
    // come through here are in the future by definition - an appointment that
    // has happened is not the next one, and a pause that has passed is not a
    // pause - and the past-only version answered "just now" for every one of
    // them, so "Automation paused until: just now" told an operator a pause
    // protecting someone mid-complaint had already expired.
    const mins = Math.round((d.getTime() - Date.now()) / 60000);
    const ahead = mins > 0;
    const away = Math.abs(mins);
    if (away < 1) return "just now";
    if (away < 60) return ahead ? `in ${away} min` : `${away} min ago`;
    if (away < 1440) {
      const h = Math.round(away / 60);
      return ahead ? `in ${h} h` : `${h} h ago`;
    }
    return d.toLocaleDateString();
  },
  name: (c: { first_name?: string; last_name?: string }) =>
    [c.first_name, c.last_name].filter(Boolean).join(" ") || "(no name)",
  /** A moment as the business reads it: Eastern, e.g. "Thu, Sep 17, 7:27 PM". */
  et: (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString("en-US", {
          timeZone: "America/New_York",
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "—",
};

/** How many people a blast is going to. A blast that builds its list at
 *  the send time has none until then, and "0 recipients" read as "this goes
 *  to nobody" - so it says when the list is made instead, or that it never
 *  was for one stopped before its time. */
export function recipientsLabel(b: {
  build_at_send?: boolean;
  total_recipients: number;
  status: string;
  matching_now?: number | null;
}) {
  if (b.build_at_send && b.total_recipients === 0) {
    if (b.status === "canceled") return "never built";
    return b.matching_now != null ? `≈${fmt.n(b.matching_now)} now` : "built at send";
  }
  return fmt.n(b.total_recipients);
}

/** A row of mutually exclusive filters, each with how many it holds.
 *
 *  The long lists - blasts, campaigns, messages - all need the same thing:
 *  narrow three hundred rows to the forty you mean, and see the size of each
 *  slice before choosing it. */
export function Chips<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ key: T; label: string; count?: number }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="row w" style={{ gap: 6 }}>
      {options.map((o) => (
        <button
          key={o.key}
          className={`btn s${o.key === value ? " p" : ""}`}
          onClick={() => onChange(o.key)}
        >
          {o.label}
          {o.count != null && (
            <span className="mono" style={{ opacity: 0.7, marginLeft: 6 }}>
              {o.count.toLocaleString("en-US")}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/** One page of an already-loaded list. Resets to page one when `resetOn`
 *  changes - staying on page 9 of a filter that now has two pages is how a
 *  list appears empty for no reason. */
export function usePaged<T>(items: T[], resetOn: unknown[], initialPerPage = 25) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(initialPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [...resetOn, perPage]);
  const pages = Math.max(1, Math.ceil(items.length / perPage));
  const current = Math.min(page, pages);
  return {
    shown: items.slice((current - 1) * perPage, current * perPage),
    page: current,
    pages,
    perPage,
    total: items.length,
    setPage,
    setPerPage,
  };
}

/** Pick several things out of a long list, by typing.
 *
 *  The audience builder offered two controls for this and neither worked once
 *  a list got long. A `<select multiple>` cannot be searched at all, which is
 *  hopeless at 120 tags. A datalist on a comma-separated box looks like it
 *  works and then stops: once the box reads "member, " the browser filters the
 *  suggestions against that WHOLE string and offers nothing, so the second
 *  value onwards has to be typed exactly from memory.
 *
 *  So: a filter box, the matching options, and what you have picked shown as
 *  chips you can remove. The value stays a comma-separated string because that
 *  is what the rule builder already reads.
 */
export function MultiPicker({
  options,
  value,
  onChange,
  placeholder = "type to search",
}: {
  options: Array<{ value: string; label: string; count?: number | null }>;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  const [q, setQ] = useState("");
  // Open on focus, not on typing. The list used to appear only once something
  // was typed, so with the box empty it was indistinguishable from a plain
  // text field - and a tag could only be chosen by someone who already knew
  // its exact name. The whole reason to offer the values is that nobody
  // remembers them.
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const chosen = value.split(",").map((s) => s.trim()).filter(Boolean);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const needle = q.trim().toLowerCase();
  const available = options.filter((o) => !chosen.includes(o.value));
  const matches = available
    .filter((o) => !needle || (o.label + " " + o.value).toLowerCase().includes(needle))
    // Most-used first when browsing, so the tags that actually hold people
    // are the ones on top rather than whatever sorts first alphabetically.
    .sort((a, b) => (b.count ?? -1) - (a.count ?? -1))
    .slice(0, 300);

  const set = (next: string[]) => onChange(next.join(", "));
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  return (
    <div
      ref={box}
      style={{ display: "inline-flex", flexDirection: "column", gap: 4, minWidth: 260, position: "relative" }}
    >
      {chosen.length > 0 && (
        <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
          {chosen.map((c) => (
            <button
              key={c}
              className="btn s"
              title="remove"
              onClick={() => set(chosen.filter((x) => x !== c))}
            >
              {labelOf(c)} ×
            </button>
          ))}
        </div>
      )}
      <div className="row" style={{ gap: 4 }}>
        <input
          className="f"
          style={{ flex: 1 }}
          value={q}
          placeholder={
            chosen.length
              ? `${chosen.length} chosen — add another`
              : `choose from ${options.length.toLocaleString()} — or ${placeholder}`
          }
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            // Enter takes the top match, so typing part of a name is enough.
            if (e.key === "Enter" && matches[0]) {
              e.preventDefault();
              set([...chosen, matches[0].value]);
              setQ("");
            }
          }}
        />
        {/* A visible affordance, because an input with no arrow does not look
            like something you can open. */}
        <button
          type="button"
          className="btn s"
          aria-label="show all"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "▴" : "▾"}
        </button>
      </div>
      {open && (
        <div
          style={{
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid var(--ln)",
            borderRadius: 6,
            background: "var(--surf, var(--bg))",
          }}
        >
          <div className="dim" style={{ padding: "5px 8px", fontSize: 11 }}>
            {needle
              ? `${matches.length.toLocaleString()} matching “${q}”`
              : `${available.length.toLocaleString()} to choose from, most used first`}
          </div>
          {matches.length === 0 && (
            <div className="dim" style={{ padding: 7, fontSize: 12 }}>
              nothing matches “{q}”
            </div>
          )}
          {matches.map((o) => (
            <div
              key={o.value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                set([...chosen, o.value]);
                setQ("");
              }}
              style={{ padding: "5px 8px", cursor: "pointer", fontSize: 12 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hv)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              {o.label}
              {o.count != null && (
                <span className="dim"> — {o.count.toLocaleString()}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
